import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import { RecipeV2Detail, CostSimulationResult } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

interface CostingTabProps {
  recipe: RecipeV2Detail;
  currencyCode?: string;
}

const CostingTab = ({ recipe, currencyCode }: CostingTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canSimulate = hasPermissionTo(['RecipeV2CostingRead']);
  const [selectedComponent, setSelectedComponent] = useState<number>(0);
  const [priceChange, setPriceChange] = useState<number>(10);
  const [simulationResult, setSimulationResult] = useState<CostSimulationResult | null>(null);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  const formatCurrency = (value: number | null | undefined) => value != null ? applyRoundAndFormat(value, currencyCode) : '—';

  const components = recipe.components ?? [];
  const totalCost = recipe.costIsComplete ? recipe.totalCost ?? null : null;
  const missingCostItems = recipe.missingCostComponentItemNumbers ?? [];

  const handleSimulate = async () => {
    if (!selectedComponent) return;
    setSimulationError(null);
    setIsSimulating(true);
    try {
      const result = await api.simulateCost(recipe.id, selectedComponent, priceChange);
      setSimulationResult(result);
    } catch (cause) {
      setSimulationResult(null);
      setSimulationError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : t('costing.simulationFailed', 'Unable to simulate cost. Please try again.'),
      );
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* Cost breakdown */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('costing.title')}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ingredient')}</TableCell>
                <TableCell align="right">{t('costing.unitCost')}</TableCell>
                <TableCell align="right">{t('quantity')}</TableCell>
                <TableCell align="right">{t('costing.lineCost')}</TableCell>
                <TableCell align="right">% of Total Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map((c) => {
                const lineCost = c.costIsComplete && c.costPrice != null ? c.costPrice * c.quantity : null;
                const pct = totalCost != null && totalCost > 0 && lineCost != null ? (lineCost / totalCost) * 100 : null;
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.componentDescription}</TableCell>
                    <TableCell align="right">{formatCurrency(c.costPrice)}</TableCell>
                    <TableCell align="right">{c.quantity.toFixed(3)}</TableCell>
                    <TableCell align="right">{formatCurrency(lineCost)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={0.5}>
                        <Typography variant="textSM">{pct != null ? `${pct.toFixed(1)}%` : '—'}</Typography>
                        <Box
                          sx={{
                            width: Math.max((pct ?? 0) * 0.8, 4),
                            height: 8,
                            borderRadius: 1,
                            bgcolor: (pct ?? 0) > 30 ? 'error.main' : (pct ?? 0) > 15 ? 'warning.main' : 'success.main',
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={600}>{t('costing.totalCost')}</Typography>
                  {!recipe.costIsComplete && (
                    <Typography variant="textXS"
                      color="warning.main">
                      {t('costing.incompleteCost', { items: missingCostItems.join(', ') })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>{formatCurrency(totalCost)}</Typography>
                </TableCell>
                <TableCell align="right">{recipe.costIsComplete && totalCost != null ? '100%' : '—'}</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={600}>{t('costing.costPerPortion')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    {recipe.costIsComplete && recipe.costPerPortion != null
                      ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4)
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* What-if simulation */}
      {canSimulate && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>
            <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('costing.simulateTitle')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            useFlexGap
            sx={{ width: '100%' }}
          >
            <Select
              size="small"
              value={selectedComponent}
              onChange={(e) => { setSelectedComponent(Number(e.target.value)); setSimulationResult(null); setSimulationError(null); }}
              displayEmpty
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 250 } }}
            >
              <MenuItem value={0}
                disabled>{t('costing.selectIngredient')}</MenuItem>
              {components.map((c) => (
                <MenuItem key={c.id}
                  value={c.id}>{c.componentDescription}</MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              type="number"
              label={t('costing.priceChange')}
              value={priceChange}
              onChange={(e) => { setPriceChange(Number(e.target.value)); setSimulationResult(null); setSimulationError(null); }}
              sx={{ width: { xs: '100%', sm: 120 } }}
              inputProps={{ step: 5 }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                height: 40,
              }}
            >
              <Button
                variant="primary"
                onClick={handleSimulate}
                disabled={!selectedComponent || isSimulating}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0,
                  transform: 'translateY(-4px)',
                }}
              >
                {t('costing.simulate')}
              </Button>
            </Box>
          </Stack>

          {simulationResult && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              useFlexGap
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <Stack spacing={0.75}
                sx={{ minWidth: { sm: 160 }, flex: 1 }}>
                <Typography variant="textSM"
                  color="text.secondary">{t('costing.originalCost')}</Typography>
                <Typography variant="textLG"
                  fontWeight={600}>{formatCurrency(simulationResult.originalCost)}</Typography>
              </Stack>
              <Stack spacing={0.75}
                sx={{ minWidth: { sm: 160 }, flex: 1 }}>
                <Typography variant="textSM"
                  color="text.secondary">{t('costing.simulatedCost')}</Typography>
                <Typography variant="textLG"
                  fontWeight={600}
                  color={simulationResult.simulatedCost > simulationResult.originalCost ? 'error.main' : 'success.main'}>
                  {formatCurrency(simulationResult.simulatedCost)}
                </Typography>
              </Stack>
              <Stack spacing={0.75}
                sx={{ minWidth: { sm: 160 }, flex: 1 }}>
                <Typography variant="textSM"
                  color="text.secondary">{t('costing.difference')}</Typography>
                <Typography variant="textLG"
                  fontWeight={600}
                  color={simulationResult.difference > 0 ? 'error.main' : 'success.main'}>
                  {simulationResult.difference > 0 ? '+' : ''}{formatCurrency(simulationResult.difference)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Paper>
      )}

      <MessagesSnackbar
        open={Boolean(simulationError)}
        onClose={() => setSimulationError(null)}
        message={simulationError ?? ''}
        severity="error"
        duration={5000}
      />
    </Stack>
  );
};

export default CostingTab;
